import { keepPreviousData } from '@tanstack/react-query';
import { flexRender } from '@tanstack/react-table';
import { format, parse } from 'date-fns';
import { EditIcon, EyeIcon, PlusIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSnapshot } from 'valtio';

import Error from '../../../components/feedback/error.jsx';
import Loading from '../../../components/feedback/loading.jsx';
import { DataTableColumnHeader } from '../../../components/table/data-table-column-header.jsx';
import { DataTableFooter } from '../../../components/table/data-table-footer.jsx';
import {
  DataTableFacetedFilter,
  DataTableToolbar,
} from '../../../components/table/data-table-toolbar.jsx';
import Heading from '../../../components/typography/heading.jsx';
import { Badge } from '../../../components/ui/badge.jsx';
import { Button } from '../../../components/ui/button.jsx';
import { Checkbox } from '../../../components/ui/checkbox.jsx';
import { DebouncedInput } from '../../../components/ui/input-debounced.jsx';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table.jsx';
import { msCatalystApi } from '../../../services/api/ms-catalyst-api.js';
import { authState } from '../../../services/state/auth-state.jsx';
import { useTable } from '../../../hooks/use-table.jsx';
import { cn } from '../../../utils/cn.js';
import {
  CAMPAIGN_COLORS,
  CAMPAIGN_STATES,
  TABLE_FILTER_LIKE,
} from '../../../utils/constants.js';
import { filterIgnoreAccents } from '../../../utils/filters.js';

const TABLE_COLUMNS = [
  {
    id: 'select',
    header: ({ table }) => (
      <div className="pr-2">
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Selecionar tudo"
          className="translate-y-[2px]"
        />
      </div>
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Selecionar linha"
        className="translate-y-[2px]"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    title: 'Name',
    id: 'name',
    accessorKey: 'name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Nome" />
    ),
    cell: ({ row }) => (
      <Link to={row.original.id}>
        <div className="min-w-48 underline">{row.getValue('name')}</div>
      </Link>
    ),
    filterFn: (row, id, value) => {
      return filterIgnoreAccents(row.getValue(id), value);
    },
    filterType: TABLE_FILTER_LIKE.BOTH,
    enableSorting: true,
    enableHiding: false,
  },
  {
    title: 'Estado',
    accessorKey: 'state',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Estado" />
    ),
    cell: ({ row }) => {
      return (
        <Badge
          className={cn(
            'min-w-40 justify-center py-1.5',
            CAMPAIGN_COLORS[row.getValue('state')],
          )}
        >
          {row.getValue('state')}
        </Badge>
      );
    },
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
  },
  {
    title: 'Data de Início',
    accessorKey: 'startDate',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Data de Início" />
    ),
    cell: ({ row }) => {
      if (row.getValue('startDate'))
        return (
          <div className="min-w-44">
            {format(row.getValue('startDate'), 'dd-MM-yyyy HH:mm:ss')}
          </div>
        );
    },
  },
  {
    title: 'Data de Fecho de Participações',
    accessorKey: 'closeDate',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Data de Fecho de Participações"
      />
    ),
    cell: ({ row }) => {
      if (row.getValue('closeDate'))
        return (
          <div>{format(row.getValue('closeDate'), 'dd-MM-yyyy HH:mm:ss')}</div>
        );
    },
  },
  {
    title: 'Data de Fim',
    accessorKey: 'endDate',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Data de Fim" />
    ),
    cell: ({ row }) => {
      if (row.getValue('endDate'))
        return (
          <div className="min-w-44">
            {format(row.getValue('endDate'), 'dd-MM-yyyy HH:mm:ss')}
          </div>
        );
    },
  },
  {
    title: 'Valor Min. de Participação',
    accessorKey: 'minEntryAmount',
    header: ({ column }) => (
      <DataTableColumnHeader
        column={column}
        title="Valor Min. de Participação (€)"
      />
    ),
    cell: ({ row }) => {
      return <div>{row.getValue('minEntryAmount')}</div>;
    },
  },
  {
    title: 'Data de Criação',
    accessorKey: 'createdTime',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Data de Criação" />
    ),
    cell: ({ row }) => {
      const dateString = row.getValue('createdTime');

      if (!dateString) {
        return <div>---</div>;
      }

      try {
        const inputFormat = 'yyyy-MM-dd HH:mm:ss:SSS';
        const dateObject = parse(dateString, inputFormat, new Date());

        return <div>{format(dateObject, 'dd-MM-yyyy HH:mm:ss')}</div>;
      } catch (error) {
        console.error('Failed to parse date:', dateString, error);

        return <div>---</div>;
      }
    },
    enableSorting: true,
    enableHiding: true,
  },
  {
    id: 'actions',
    cell: CellActions,
  },
];

function CellActions({ row }) {
  const { isAdmin } = useSnapshot(authState);

  return (
    <div className="flex flex-row space-x-2">
      <Link to={row.original.id}>
        <Button
          variant="outline"
          className="h-auto p-2"
          aria-label="Ver campanha"
        >
          <EyeIcon />
        </Button>
      </Link>

      {isAdmin &&
      row.original.state !== CAMPAIGN_STATES.READY_TO_DRAW &&
      row.original.state !== CAMPAIGN_STATES.CONCLUDED ? (
        <Link to={`edit/${row.original.id}`}>
          <Button
            variant="outline"
            className="h-auto p-2"
            aria-label="Editar campanha"
          >
            <EditIcon />
          </Button>
        </Link>
      ) : null}
    </div>
  );
}

export function CampaignsList() {
  const { isAdmin } = useSnapshot(authState);

  const {
    table,
    query: queryCampaigns,
    filters,
  } = useTable(
    TABLE_COLUMNS,
    {
      queryKey: ['campaigns'],
      queryFn: async () => {
        const { data: campaigns } = await msCatalystApi.get(
          `campaigns/paginate?${filters}`,
        );

        return campaigns.data;
      },
      placeholderData: keepPreviousData,
      select: (data) => {
        return {
          ...data,
          rows: data.rows.map((item) => ({
            ...item,
            id: item.ROWID,
            createdTime: item.CREATEDTIME,
          })),
        };
      },
    },
    {
      sorting: [
        {
          id: 'createdTime',
          desc: true,
        },
      ],
    },
  );

  return (
    <>
      <div className="flex flex-col items-start justify-start gap-y-6 pb-6 lg:flex-row lg:items-center lg:justify-between lg:gap-y-0">
        <Heading>Campanhas</Heading>

        {isAdmin ? (
          <Button asChild>
            <Link to="create">
              <PlusIcon />
              Criar Campanha
            </Link>
          </Button>
        ) : null}
      </div>

      {queryCampaigns.isLoading || queryCampaigns.isPending ? (
        <div className="flex h-[70dvh] items-center justify-center">
          <Loading />
        </div>
      ) : queryCampaigns.isError ? (
        <Error />
      ) : (
        <div className="space-y-4">
          {/* TABLE HEADER */}
          <DataTableToolbar
            table={table}
            actions={
              isAdmin ? { route: 'campaigns', useQuery: queryCampaigns } : null
            }
          >
            <DebouncedInput
              placeholder="Pesquisar nome..."
              value={table.getColumn('name')?.getFilterValue() ?? ''}
              onChange={(val) => table.getColumn('name')?.setFilterValue(val)}
              className="h-auto w-[150px] lg:w-[255px]"
            />

            {table.getColumn('state') && (
              <DataTableFacetedFilter
                column={table.getColumn('state')}
                title="Estado"
                options={Object.values(CAMPAIGN_STATES).map((val) => ({
                  label: val,
                  value: val,
                }))}
              />
            )}
          </DataTableToolbar>
          {/* TABLE HEADER */}

          {/* TABLE */}
          <div className="rounded-md border border-muted-foreground/35">
            <Table>
              <TableHeader className="bg-muted/20">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead key={header.id} colSpan={header.colSpan}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {queryCampaigns.isRefetching ? (
                  <TableRow>
                    <TableCell
                      colSpan={TABLE_COLUMNS.length}
                      className="h-24 text-center"
                    >
                      <Loading />
                    </TableCell>
                  </TableRow>
                ) : queryCampaigns.isRefetchError ? (
                  <TableRow>
                    <TableCell
                      colSpan={TABLE_COLUMNS.length}
                      className="h-24 text-center"
                    >
                      <Error />
                    </TableCell>
                  </TableRow>
                ) : table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && 'selected'}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={TABLE_COLUMNS.length}
                      className="h-24 text-center"
                    >
                      {table.getState().columnFilters?.length ||
                      table.getState().globalFilter?.length
                        ? 'Não foram encontrados resultados.'
                        : 'Não existem quaisquer Campanhas na base de dados.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          {/* TABLE */}

          {/* TABLE FOOTER */}
          <DataTableFooter table={table} />
          {/* TABLE FOOTER */}
        </div>
      )}
    </>
  );
}
